import { useEffect, useRef } from 'react'
import { Avatar, Box, Dialog, IconButton, Tooltip, Typography, alpha, useTheme } from '@mui/material'
import { ChevronLeft, ChevronRight, Close, Download, OpenInNew } from '@mui/icons-material'

import AuthImage from './AuthImage'
import { avatarColor, avatarSrc, getInitials } from '../../utils/avatar'

const imageUrl = (m) => `${process.env.REACT_APP_SERVER_URL}${m.attachment.url}`

const formatWhen = (d) =>
  new Date(d).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })

// Full-screen photo viewer like Teams: dark backdrop, sender + date on top,
// download / open / close on the right, arrows and a thumbnail strip to move between photos.
// items: messages with an image attachment (newest first); index: the one shown, null = closed
const PhotoViewer = ({ items, index, onIndexChange, onClose, onDownload, onOpen, userById, meId }) => {
  const theme = useTheme()
  const accent = theme.palette.secondary.main
  const stripRef = useRef(null)

  const open = index !== null && index >= 0 && index < items.length
  const current = open ? items[index] : null

  // Newest first in the grid, so "previous" (left) is the newer photo, like scrolling back in Teams
  const hasPrev = open && index > 0
  const hasNext = open && index < items.length - 1

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'ArrowLeft' && hasPrev) onIndexChange(index - 1)
      else if (e.key === 'ArrowRight' && hasNext) onIndexChange(index + 1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, index, hasPrev, hasNext, onIndexChange])

  // Keep the selected thumbnail in view
  useEffect(() => {
    if (!open) return
    stripRef.current?.querySelector(`[data-idx="${index}"]`)?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' })
  }, [open, index])

  if (!current) return null

  const sender = userById[current.senderId]
  const senderName = current.senderId === meId ? 'You' : sender?.name || 'Someone'

  const barButtonSx = {
    color: 'rgba(255,255,255,0.85)',
    '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' },
  }

  const arrowSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 48,
    height: 48,
    color: '#fff',
    backgroundColor: 'rgba(255,255,255,0.10)',
    backdropFilter: 'blur(4px)',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.22)' },
  }

  return (
    <Dialog
      open
      fullScreen
      onClose={onClose}
      slotProps={{ paper: { sx: { backgroundColor: 'rgba(12,12,14,0.96)', color: '#fff' } } }}
    >
      {/* Top bar: who shared it and when, then actions */}
      <Box
        display="flex"
        alignItems="center"
        gap={1.5}
        px={2}
        py={1.25}
        sx={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
      >
        <Avatar
          src={sender ? avatarSrc(sender.avatar) : undefined}
          sx={{ width: 36, height: 36, ...(sender ? avatarColor(sender.name, 'dark') : {}) }}
        >
          {getInitials(sender?.name || senderName)}
        </Avatar>
        <Box minWidth={0} flex={1}>
          <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
            {senderName}
          </Typography>
          <Typography variant="caption" noWrap display="block" sx={{ color: 'rgba(255,255,255,0.65)' }}>
            {formatWhen(current.createdAt)} · {current.attachment.name}
          </Typography>
        </Box>

        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', mr: 1, display: { xs: 'none', sm: 'block' } }}>
          {index + 1} / {items.length}
        </Typography>

        <Tooltip title="Download">
          <IconButton onClick={() => onDownload(current)} sx={barButtonSx}>
            <Download />
          </IconButton>
        </Tooltip>
        <Tooltip title="Open in new tab">
          <IconButton onClick={() => onOpen(current)} sx={{ ...barButtonSx, display: { xs: 'none', sm: 'inline-flex' } }}>
            <OpenInNew />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close (Esc)">
          <IconButton onClick={onClose} sx={barButtonSx}>
            <Close />
          </IconButton>
        </Tooltip>
      </Box>

      {/* The photo; clicking the dark area around it closes the viewer */}
      <Box
        flex={1}
        minHeight={0}
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={{ xs: 1, md: 4 }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      >
        <AuthImage
          key={current._id}
          src={imageUrl(current)}
          alt={current.attachment.name}
          sx={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            borderRadius: 1,
            boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
            userSelect: 'none',
          }}
        />

        {hasPrev && (
          <IconButton aria-label="Previous photo" onClick={() => onIndexChange(index - 1)} sx={{ ...arrowSx, left: { xs: 8, md: 24 } }}>
            <ChevronLeft fontSize="large" />
          </IconButton>
        )}
        {hasNext && (
          <IconButton aria-label="Next photo" onClick={() => onIndexChange(index + 1)} sx={{ ...arrowSx, right: { xs: 8, md: 24 } }}>
            <ChevronRight fontSize="large" />
          </IconButton>
        )}
      </Box>

      {/* Thumbnail strip */}
      {items.length > 1 && (
        <Box
          ref={stripRef}
          display="flex"
          gap={1}
          px={2}
          py={1.25}
          justifyContent={{ xs: 'flex-start', md: 'center' }}
          sx={{
            overflowX: 'auto',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(255,255,255,0.2) transparent',
          }}
        >
          {items.map((m, i) => (
            <Box
              key={m._id}
              data-idx={i}
              onClick={() => onIndexChange(i)}
              sx={{
                width: 56,
                height: 56,
                flexShrink: 0,
                borderRadius: 1,
                overflow: 'hidden',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                outline: i === index ? `2px solid ${accent}` : '2px solid transparent',
                outlineOffset: 1,
                opacity: i === index ? 1 : 0.55,
                transition: 'opacity .15s',
                backgroundColor: alpha('#fff', 0.06),
                '&:hover': { opacity: 1 },
              }}
            >
              <AuthImage
                src={imageUrl(m)}
                alt={m.attachment.name}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </Box>
          ))}
        </Box>
      )}
    </Dialog>
  )
}

export default PhotoViewer
